import { useState, useCallback } from 'react';
import { Modal, message } from 'antd';
import { d206Api, FD160 } from '../../../services/d206Api';

interface UseD206ApprovalOptions {
  record: FD160 | null;
  isEditing: boolean;
  loadRecord: (id: string) => Promise<void>;
}

export function useD206Approval({ record, isEditing, loadRecord }: UseD206ApprovalOptions) {
  const [approvalHistoryVisible, setApprovalHistoryVisible] = useState(false);

  const handleSubmit = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認送審',
      content: `確定要將繳庫退出單 ${record.fd160_01} 送審？`,
      okText: '送審',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await d206Api.submitForApproval(record.fd160_01);
          message.success(resp.message || '送審成功');
          await loadRecord(record.fd160_01);
        } catch (err: unknown) {
          const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '送審失敗');
          message.error(msg);
        }
      },
    });
  }, [record, isEditing, loadRecord]);

  const handleApprove = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認核准',
      content: `確定要核准繳庫退出單 ${record.fd160_01}？`,
      okText: '核准',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await d206Api.approve(record.fd160_01);
          message.success(resp.message || '核准成功');
          await loadRecord(record.fd160_01);
        } catch (err: unknown) {
          const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '核准失敗');
          message.error(msg);
        }
      },
    });
  }, [record, isEditing, loadRecord]);

  const handleReject = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認退回',
      content: `確定要退回繳庫退出單 ${record.fd160_01}？`,
      okText: '退回',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await d206Api.reject(record.fd160_01);
          message.success(resp.message || '退回成功');
          await loadRecord(record.fd160_01);
        } catch (err: unknown) {
          const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '退回失敗');
          message.error(msg);
        }
      },
    });
  }, [record, isEditing, loadRecord]);

  const handleWriteoff = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認沖銷',
      content: `確定要沖銷此單據 ${record.fd160_01}？沖銷後可重新送審。`,
      okText: '沖銷',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await d206Api.writeoff(record.fd160_01);
          message.success(resp.message || '沖銷成功');
          await loadRecord(record.fd160_01);
        } catch (err: unknown) {
          const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '沖銷失敗');
          message.error(msg);
        }
      },
    });
  }, [record, isEditing, loadRecord]);

  const handleViewHistory = useCallback(() => {
    if (!record) return;
    setApprovalHistoryVisible(true);
  }, [record]);

  return {
    approvalHistoryVisible,
    setApprovalHistoryVisible,
    handleSubmit,
    handleApprove,
    handleReject,
    handleWriteoff,
    handleViewHistory,
  };
}
